import { useState, useEffect } from 'react';
import { createSampleFileTree } from './sampleData';
import { MarkdownExplorer } from '../../src/components/MarkdownExplorer';
import { CustomFolderRenderer } from './CustomFolderRenderer';
import { handleDirectNavigation, findNodeByPath } from './utils/urlHandler';
import { fetchMarkdownContent } from './utils/contentFetcher';
import './App.css';
import './sidebar-scroll.css';
import './folder-content.css';

function App() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [initialRoute, setInitialRoute] = useState<string>('/');
  const fileTree = createSampleFileTree();

  // Handle direct navigation to markdown files
  useEffect(() => {
    // Get the current URL path and normalize it
    const path = handleDirectNavigation(window.location.pathname);
    
    // Check if the path exists in our file tree
    const node = findNodeByPath(fileTree, path);
    
    if (node) {
      // If the node exists, set it as the initial route
      setInitialRoute(path);
    } else if (path !== '/') {
      // If the node doesn't exist and it's not the root path, redirect to root
      console.warn(`Path not found: ${path}, redirecting to root`);
      window.history.replaceState({}, '', '/');
      setInitialRoute('/');
    }
  }, [fileTree]);

  // Handle navigation events
  const handleNavigate = (path: string) => {
    // Update the browser URL without causing a page reload
    window.history.pushState({}, '', path);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <div className={`app ${theme}`}>
      <header className="app-header">
        <h1>Markdown Explorer Viewer Demo</h1>
        <button 
          onClick={toggleTheme}
          className="theme-toggle"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
      </header>
      
      <main className="app-main">
        <MarkdownExplorer
          fileTree={fileTree}
          theme={theme}
          enableSearch={true}
          showBreadcrumbs={true}
          className="demo-explorer"
          disableAutoSelect={true}
          initialRoute={initialRoute}
          onNavigate={(path, node) => handleNavigate(path)}
          contentFetcher={fetchMarkdownContent}
          renderFolderContent={({currentNode, onNodeClick}) => (
            <CustomFolderRenderer 
              currentNode={currentNode} 
              onNodeClick={onNodeClick} 
              theme={theme}
            />
          )}
        />
      </main>
      
      <footer className="app-footer">
        <p>
          Built with ❤️ using the{' '}
          <strong>@asafarim/markdown-explorer-viewer</strong> package
        </p>
      </footer>
    </div>
  );
}

export default App;
